import React, { useEffect, useState } from 'react';

function Inventory({ token, selectedPropertyId }) {
  const emptyForm = {
    item_code: '',
    store_id: '',
    uom_id: '',
    uom_value: '',
    available_quantity_ml: '',
    available_quantity_bottles: '',
    reorder_level: '',
    reorder_quantity: ''
  };
  const [items, setItems] = useState([]);
  const [masterItems, setMasterItems] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [stores, setStores] = useState([]);
  const [error, setError] = useState('');
  const [editingCode, setEditingCode] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [purchaseForm, setPurchaseForm] = useState({ entry_mode: 'MAIN_ITEM', item_code: '', menu_item_id: '', purchased_bottles: '' });
  const [adjustmentForm, setAdjustmentForm] = useState({
    entry_mode: 'MAIN_ITEM',
    item_code: '',
    menu_item_id: '',
    adjustment_mode: 'BOTTLE',
    direction: 'DEDUCT',
    adjustment_value: '',
    reason: ''
  });
  const resolvePropertyId = () => String(selectedPropertyId || localStorage.getItem('selectedPropertyId') || '').trim();
  const authHeaders = (extra = {}) => ({ Authorization: `Bearer ${token}`, 'x-property-id': resolvePropertyId(), ...extra });
  const parseResponse = async (res) => {
    const raw = await res.text();
    try {
      const json = raw ? JSON.parse(raw) : {};
      return { data: json, message: json?.error || '' };
    } catch {
      return { data: {}, message: raw || '' };
    }
  };

  const getUomById = (id) => uoms.find((u) => String(u.id) === String(id)) || null;
  const isMlPerBottleUomId = (id) => String(getUomById(id)?.uom_name || '').trim().toLowerCase() === 'ml per bottle';
  const getDefaultMlPerBottleUomId = () => String(uoms.find((u) => String(u.uom_name || '').trim().toLowerCase() === 'ml per bottle')?.id || '');
  const calcQtyMl = (next) => {
    const qty = Number(next.available_quantity_bottles || 0);
    const value = Number(next.uom_value || 0);
    return isMlPerBottleUomId(next.uom_id) ? (qty > 0 && value > 0 ? String(qty * value) : '') : (qty > 0 ? String(qty) : '');
  };

  useEffect(() => {
    if (!resolvePropertyId()) {
      setItems([]);
      setMasterItems([]);
      setMenuItems([]);
      setUoms([]);
      setStores([]);
      setError('Select a property first from Hotel Config');
      return;
    }
    setError('');
    loadInventory();
    loadItemMaster();
    loadMenuSetup();
    loadUoms();
    loadStores();
  }, [token, selectedPropertyId]);

  const loadInventory = () => {
    if (!resolvePropertyId()) {
      setItems([]);
      return;
    }
    fetch('/api/inventory', { headers: authHeaders() })
      .then(res => res.json())
      .then(data => setItems(Array.isArray(data) ? data : []))
      .catch(() => {
        setError('Failed to load inventory');
        setItems([]);
      });
  };

  const loadItemMaster = () => {
    if (!resolvePropertyId()) return setMasterItems([]);
    fetch('/api/item-master', { headers: authHeaders() })
      .then(res => res.json())
      .then(data => setMasterItems(Array.isArray(data) ? data : []))
      .catch(() => setMasterItems([]));
  };

  const loadMenuSetup = () => {
    if (!resolvePropertyId()) return setMenuItems([]);
    fetch('/api/inventory/menu-items', { headers: authHeaders() })
      .then(res => res.json())
      .then(data => setMenuItems(Array.isArray(data) ? data : []))
      .catch(() => setMenuItems([]));
  };

  const loadUoms = () => {
    fetch('/api/uom-master', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setUoms(Array.isArray(data) ? data.filter((u) => u.is_active !== false) : []))
      .catch(() => setUoms([]));
  };

  const loadStores = () => {
    if (!resolvePropertyId()) return setStores([]);
    fetch('/api/store-master', { headers: authHeaders() })
      .then(res => res.json())
      .then(data => setStores(Array.isArray(data) ? data.filter((s) => s.is_active !== false) : []))
      .catch(() => setStores([]));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'item_code') {
      const selected = masterItems.find((i) => i.main_item_code === value);
      const defaultUomId = form.uom_id || getDefaultMlPerBottleUomId();
      const next = {
        ...form,
        item_code: value,
        uom_id: defaultUomId,
        uom_value: form.uom_value || selected?.quantity_ml_per_unit || ''
      };
      next.available_quantity_ml = calcQtyMl(next);
      setForm(next);
      return;
    }

    if (name === 'available_quantity_bottles' || name === 'uom_value' || name === 'uom_id') {
      const next = { ...form, [name]: value };
      if (name === 'uom_id' && !isMlPerBottleUomId(value)) next.uom_value = '';
      next.available_quantity_ml = calcQtyMl(next);
      setForm(next);
      return;
    }

    setForm({ ...form, [name]: value });
  };

  const validateForm = () => {
    if (!form.item_code) return 'Please select an item from Item Master before adding inventory.';
    if (!form.store_id) return 'Please select a Store.';
    if (!form.uom_id) return 'Please select a UOM.';
    if (Number(form.available_quantity_bottles || 0) < 0) return 'Enter valid quantity.';
    if (isMlPerBottleUomId(form.uom_id) && Number(form.uom_value || 0) <= 0) return 'Enter UOM value for ML Per Bottle.';
    return '';
  };

  const handleCreate = async () => {
    if (!resolvePropertyId()) return setError('Select a property first from Hotel Config');
    const validation = validateForm();
    if (validation) return setError(validation);
    try {
      const res = await fetch('/api/inventory', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });
      const { data, message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Create failed');
      setItems([...items, data]);
      setForm(emptyForm);
      setError('');
    } catch {
      setError('Network error');
    }
  };

  const handleEdit = (item) => {
    const masterItem = masterItems.find((m) => m.main_item_code === item.item_code);
    const next = {
      item_code: item.item_code,
      store_id: item.store_id || '',
      uom_id: item.uom_id || getDefaultMlPerBottleUomId(),
      uom_value: item.uom_value || item.ml_per_bottle || masterItem?.quantity_ml_per_unit || '',
      available_quantity_bottles: item.available_quantity_bottles || '',
      available_quantity_ml: '',
      reorder_level: item.reorder_level || '',
      reorder_quantity: item.reorder_quantity || ''
    };
    next.available_quantity_ml = calcQtyMl(next);
    setEditingCode(item.item_code);
    setForm(next);
  };

  const handleUpdate = async () => {
    if (!resolvePropertyId()) return setError('Select a property first from Hotel Config');
    const validation = validateForm();
    if (validation) return setError(validation);
    try {
      const res = await fetch(`/api/inventory/${editingCode}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });
      const { data, message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Update failed');
      setItems(items.map((i) => (i.item_code === editingCode ? data : i)));
      setForm(emptyForm);
      setEditingCode(null);
      setError('');
    } catch {
      setError('Network error');
    }
  };

  const handleDelete = async (item_code) => {
    try {
      if (!resolvePropertyId()) return setError('Select a property first from Hotel Config');
      const res = await fetch(`/api/inventory/${item_code}`, { method: 'DELETE', headers: authHeaders() });
      const { message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Delete failed');
      setItems(items.filter((i) => i.item_code !== item_code));
      setError('');
    } catch {
      setError('Network error');
    }
  };

  const handlePurchaseAdd = async () => {
    if (!resolvePropertyId()) return setError('Select a property first from Hotel Config');
    if (purchaseForm.entry_mode === 'MAIN_ITEM' && !purchaseForm.item_code) return setError('Please select an inventory item for purchase entry.');
    if (purchaseForm.entry_mode === 'MENU_ITEM' && !purchaseForm.menu_item_id) return setError('Please select a menu item for purchase entry.');
    if (!purchaseForm.purchased_bottles || Number(purchaseForm.purchased_bottles) <= 0) return setError('Purchased quantity must be greater than zero.');
    try {
      const res = await fetch('/api/inventory/purchase', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          item_code: purchaseForm.entry_mode === 'MAIN_ITEM' ? purchaseForm.item_code : undefined,
          menu_item_id: purchaseForm.entry_mode === 'MENU_ITEM' ? Number(purchaseForm.menu_item_id) : undefined,
          purchased_bottles: Number(purchaseForm.purchased_bottles || 0)
        })
      });
      const { data, message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Purchase entry failed');
      setItems(items.map((i) => (i.item_code === data.item_code ? data : i)));
      setPurchaseForm({ entry_mode: 'MAIN_ITEM', item_code: '', menu_item_id: '', purchased_bottles: '' });
      setError('');
    } catch {
      setError('Network error');
    }
  };

  const handleStockAdjustment = async () => {
    if (!resolvePropertyId()) return setError('Select a property first from Hotel Config');
    if (adjustmentForm.entry_mode === 'MAIN_ITEM' && !adjustmentForm.item_code) return setError('Please select an inventory item for stock adjustment.');
    if (adjustmentForm.entry_mode === 'MENU_ITEM' && !adjustmentForm.menu_item_id) return setError('Please select a menu item for stock adjustment.');
    const adjustmentValue = Number(adjustmentForm.adjustment_value || 0);
    if (Number.isNaN(adjustmentValue) || adjustmentValue <= 0) return setError('Adjustment value must be greater than zero.');

    try {
      const res = await fetch('/api/inventory/adjust', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          item_code: adjustmentForm.entry_mode === 'MAIN_ITEM' ? adjustmentForm.item_code : undefined,
          menu_item_id: adjustmentForm.entry_mode === 'MENU_ITEM' ? Number(adjustmentForm.menu_item_id) : undefined,
          adjustment_mode: adjustmentForm.adjustment_mode,
          direction: adjustmentForm.direction,
          adjustment_value: adjustmentValue,
          reason: adjustmentForm.reason
        })
      });
      const { data, message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Stock adjustment failed');
      setItems(items.map((i) => (i.item_code === data?.item?.item_code ? data.item : i)));
      setAdjustmentForm({ entry_mode: 'MAIN_ITEM', item_code: '', menu_item_id: '', adjustment_mode: 'BOTTLE', direction: 'DEDUCT', adjustment_value: '', reason: '' });
      setError('');
    } catch {
      setError('Network error');
    }
  };

  const availableItemOptions = masterItems.filter((masterItem) => editingCode || !items.some((invItem) => invItem.item_code === masterItem.main_item_code));
  const editingItemName = editingCode ? (masterItems.find((m) => m.main_item_code === form.item_code)?.main_item_name || '') : '';
  const getStoreName = (item) => stores.find((s) => String(s.id) === String(item.store_id))?.store_name || item.store_name || '-';
  const getUomName = (item) => getUomById(item.uom_id)?.uom_name || item.uom_name || '-';
  const inventoryCodeSet = new Set((items || []).map((i) => String(i.item_code || '').trim()));
  const menuItemOptions = (menuItems || []).filter((m) => inventoryCodeSet.has(String(m.main_item_code || '').trim()));
  const mapMenuToMainItemCode = (menuItemId) => String(menuItems.find((m) => String(m.id) === String(menuItemId))?.main_item_code || '').trim();
  const mappedPurchaseItemCode = purchaseForm.entry_mode === 'MENU_ITEM' ? mapMenuToMainItemCode(purchaseForm.menu_item_id) : purchaseForm.item_code;
  const mappedAdjustmentItemCode = adjustmentForm.entry_mode === 'MENU_ITEM' ? mapMenuToMainItemCode(adjustmentForm.menu_item_id) : adjustmentForm.item_code;

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>Inventory Management</h2>
      {error && <div className="error-banner">{error}</div>}

      <div className="filter-bar" style={{ marginBottom: '2rem' }}>
        <h3>{editingCode ? 'Edit Item' : 'Add New Item'}</h3>
        <form style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:'1rem' }}>
          {editingCode ? (
            <input name="item_code_display" value={editingItemName ? `${form.item_code} - ${editingItemName}` : form.item_code} disabled style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1', background:'#ecf0f1' }} />
          ) : (
            <select name="item_code" value={form.item_code} onChange={handleChange} style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}>
              <option value="">-- Select Item from Item Master --</option>
              {availableItemOptions.map((masterItem) => (
                <option key={masterItem.main_item_code} value={masterItem.main_item_code}>{masterItem.main_item_code} - {masterItem.main_item_name}</option>
              ))}
            </select>
          )}
          <select name="store_id" value={form.store_id} onChange={handleChange} style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}>
            <option value="">-- Select Store --</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
          </select>
          <select name="uom_id" value={form.uom_id} onChange={handleChange} style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}>
            <option value="">-- Select UOM --</option>
            {uoms.map((u) => <option key={u.id} value={u.id}>{u.uom_name}</option>)}
          </select>
          <input name="uom_value" placeholder="UOM Value" value={form.uom_value} onChange={handleChange} disabled={!isMlPerBottleUomId(form.uom_id)} style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1', background: !isMlPerBottleUomId(form.uom_id) ? '#ecf0f1' : '#fff' }} />
          <input name="available_quantity_bottles" placeholder="Quantity" value={form.available_quantity_bottles} onChange={handleChange} style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }} />
          <input name="available_quantity_ml" placeholder="Quantity in Base Qty (Auto)" value={form.available_quantity_ml} readOnly style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1', background:'#ecf0f1' }} />
          <input name="reorder_level" placeholder="Reorder Level" value={form.reorder_level} onChange={handleChange} style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }} />
          <input name="reorder_quantity" placeholder="Reorder Quantity" value={form.reorder_quantity} onChange={handleChange} style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }} />
        </form>
        <div style={{ marginTop:'1rem', display:'flex', gap:'1rem' }}>
          {editingCode ? (
            <>
              <button type="button" onClick={handleUpdate} className="btn-success" style={{ padding:'0.75rem 2rem', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'1rem' }}>Update Item</button>
              <button type="button" onClick={() => { setEditingCode(null); setForm(emptyForm); }} className="btn-muted" style={{ padding:'0.75rem 2rem', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'1rem' }}>Cancel</button>
            </>
          ) : (
            <button type="button" onClick={handleCreate} className="btn-primary" style={{ padding:'0.75rem 2rem', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'1rem' }}>Add Item</button>
          )}
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: '2rem' }}>
        <h3>Purchase Entry (Add Stock)</h3>
        <form style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:'1rem' }}>
          <select
            value={purchaseForm.entry_mode}
            onChange={(e) => setPurchaseForm({ ...purchaseForm, entry_mode: e.target.value, item_code: '', menu_item_id: '' })}
            style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}
          >
            <option value="MAIN_ITEM">Entry By Main Item</option>
            <option value="MENU_ITEM">Entry By Menu Item</option>
          </select>
          {purchaseForm.entry_mode === 'MAIN_ITEM' ? (
            <select name="item_code" value={purchaseForm.item_code} onChange={(e) => setPurchaseForm({ ...purchaseForm, item_code: e.target.value })} style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}>
              <option value="">-- Select Inventory Item --</option>
              {items.map((item) => <option key={item.item_code} value={item.item_code}>{item.item_code} ({getStoreName(item)})</option>)}
            </select>
          ) : (
            <select name="menu_item_id" value={purchaseForm.menu_item_id} onChange={(e) => setPurchaseForm({ ...purchaseForm, menu_item_id: e.target.value })} style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}>
              <option value="">-- Select Menu Item --</option>
              {menuItemOptions.map((m) => <option key={m.id} value={m.id}>{m.menu_item_name || m.menu_item_code} ({m.main_item_code})</option>)}
            </select>
          )}
          <input value={mappedPurchaseItemCode || '-'} readOnly placeholder="Mapped Main Item Code" style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1', background:'#ecf0f1' }} />
          <input name="purchased_bottles" placeholder="Purchased Quantity" value={purchaseForm.purchased_bottles} onChange={(e) => setPurchaseForm({ ...purchaseForm, purchased_bottles: e.target.value })} style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }} />
        </form>
        <div style={{ marginTop:'1rem' }}>
          <button type="button" onClick={handlePurchaseAdd} className="btn-primary" style={{ padding:'0.75rem 2rem', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'1rem' }}>Add Purchase to Inventory</button>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: '2rem' }}>
        <h3>Stock Adjustment (Bottle / ML)</h3>
        <form style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:'1rem' }}>
          <select
            value={adjustmentForm.entry_mode}
            onChange={(e) => setAdjustmentForm({ ...adjustmentForm, entry_mode: e.target.value, item_code: '', menu_item_id: '' })}
            style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}
          >
            <option value="MAIN_ITEM">Entry By Main Item</option>
            <option value="MENU_ITEM">Entry By Menu Item</option>
          </select>
          {adjustmentForm.entry_mode === 'MAIN_ITEM' ? (
            <select
              value={adjustmentForm.item_code}
              onChange={(e) => setAdjustmentForm({ ...adjustmentForm, item_code: e.target.value })}
              style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}
            >
              <option value="">-- Select Inventory Item --</option>
              {items.map((item) => <option key={item.item_code} value={item.item_code}>{item.item_code} ({getStoreName(item)})</option>)}
            </select>
          ) : (
            <select
              value={adjustmentForm.menu_item_id}
              onChange={(e) => setAdjustmentForm({ ...adjustmentForm, menu_item_id: e.target.value })}
              style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}
            >
              <option value="">-- Select Menu Item --</option>
              {menuItemOptions.map((m) => <option key={m.id} value={m.id}>{m.menu_item_name || m.menu_item_code} ({m.main_item_code})</option>)}
            </select>
          )}
          <input value={mappedAdjustmentItemCode || '-'} readOnly placeholder="Mapped Main Item Code" style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1', background:'#ecf0f1' }} />
          <select
            value={adjustmentForm.adjustment_mode}
            onChange={(e) => setAdjustmentForm({ ...adjustmentForm, adjustment_mode: e.target.value })}
            style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}
          >
            <option value="BOTTLE">Full Bottle</option>
            <option value="ML">ML</option>
          </select>
          <select
            value={adjustmentForm.direction}
            onChange={(e) => setAdjustmentForm({ ...adjustmentForm, direction: e.target.value })}
            style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}
          >
            <option value="DEDUCT">Deduct</option>
            <option value="ADD">Add</option>
          </select>
          <input
            type="number"
            step="0.001"
            value={adjustmentForm.adjustment_value}
            onChange={(e) => setAdjustmentForm({ ...adjustmentForm, adjustment_value: e.target.value })}
            placeholder={adjustmentForm.adjustment_mode === 'ML' ? 'Adjustment (ML)' : 'Adjustment (Bottle)'}
            style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}
          />
          <input
            value={adjustmentForm.reason}
            onChange={(e) => setAdjustmentForm({ ...adjustmentForm, reason: e.target.value })}
            placeholder="Reason (optional)"
            style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}
          />
        </form>
        <div style={{ marginTop:'1rem' }}>
          <button type="button" onClick={handleStockAdjustment} className="btn-success" style={{ padding:'0.75rem 2rem', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'1rem' }}>Apply Stock Adjustment</button>
        </div>
      </div>

      <h3>Current Inventory</h3>
      {!items || items.length === 0 ? (
        <p>No items in inventory</p>
      ) : (
        <table style={{ width:'100%', borderCollapse:'collapse', background:'#fff' }}>
          <thead>
            <tr style={{ background:'var(--theme-primary-dark)', color:'#fff' }}>
              <th style={{ padding:'0.75rem', textAlign:'left', borderBottom:'2px solid #dcdde1' }}>Item Code</th>
              <th style={{ padding:'0.75rem', textAlign:'left', borderBottom:'2px solid #dcdde1' }}>Main Item Name</th>
              <th style={{ padding:'0.75rem', textAlign:'left', borderBottom:'2px solid #dcdde1' }}>Store</th>
              <th style={{ padding:'0.75rem', textAlign:'left', borderBottom:'2px solid #dcdde1' }}>UOM</th>
              <th style={{ padding:'0.75rem', textAlign:'left', borderBottom:'2px solid #dcdde1' }}>UOM Value</th>
              <th style={{ padding:'0.75rem', textAlign:'left', borderBottom:'2px solid #dcdde1' }}>Quantity (Base)</th>
              <th style={{ padding:'0.75rem', textAlign:'left', borderBottom:'2px solid #dcdde1' }}>Quantity</th>
              <th style={{ padding:'0.75rem', textAlign:'left', borderBottom:'2px solid #dcdde1' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.item_code} style={{ borderBottom:'1px solid #dcdde1' }}>
                <td style={{ padding:'0.75rem' }}>{item.item_code}</td>
                <td style={{ padding:'0.75rem' }}>{masterItems.find((m) => m.main_item_code === item.item_code)?.main_item_name || '-'}</td>
                <td style={{ padding:'0.75rem' }}>{getStoreName(item)}</td>
                <td style={{ padding:'0.75rem' }}>{getUomName(item)}</td>
                <td style={{ padding:'0.75rem' }}>{item.uom_value || item.ml_per_bottle || '-'}</td>
                <td style={{ padding:'0.75rem' }}>{item.available_quantity_ml}</td>
                <td style={{ padding:'0.75rem' }}>{item.available_quantity_bottles}</td>
                <td style={{ padding:'0.75rem', display:'flex', gap:'0.5rem' }}>
                  <button onClick={() => handleEdit(item)} style={{ background:'#3498db', color:'#fff', border:'none', padding:'0.5rem 1rem', borderRadius:'4px', cursor:'pointer' }}>Edit</button>
                  <button onClick={() => handleDelete(item.item_code)} className="btn-danger" style={{ border:'none', padding:'0.5rem 1rem', borderRadius:'4px', cursor:'pointer' }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default Inventory;
